import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { X, Copy, Loader2, Check, Mail } from 'lucide-react';
import { canCreatePortalLink, canSendPortalInvite } from '../../utils/permissions';

// s8portal-4e7b2c95 — S8a (founder decision, Marcus 2026-10-04): getting a copyable link and
// sending an invite are separate. Owner / Admin / Manager may do both; Office Staff and Sales
// Consultants may only email an invite to the customer's address on file (sendPortalInvite never
// returns the link); Field Technicians never reach this modal. The server enforces the same split.
const PortalInviteModal = ({ isOpen, onClose, customer, orgId, currentUserProfile }) => {
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [magicLink, setMagicLink] = useState('');
    const [sentTo, setSentTo] = useState('');
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const mayCopyLink = canCreatePortalLink(currentUserProfile);
    const mayInvite = canSendPortalInvite(currentUserProfile);
    const emailOnFile = (customer?.email || '').trim();

    const generateLink = async () => {
        setLoading(true);
        setError('');
        try {
            const generateToken = httpsCallable(functions, 'generateCustomerPortalToken');
            const result = await generateToken({
                organizationId: orgId,
                customerId: customer.id
            });

            const token = result.data.token;
            // Construct the full URL
            const baseUrl = window.location.origin;
            const link = `${baseUrl}/portal/login/${token}`;

            setMagicLink(link);
        } catch (err) {
            console.error("Error generating token:", err);
            setError(err.message || "Failed to generate invite link.");
        } finally {
            setLoading(false);
        }
    };

    const sendInvite = async () => {
        setSending(true);
        setError('');
        try {
            const send = httpsCallable(functions, 'sendPortalInvite');
            const result = await send({ customerId: customer.id });
            setSentTo(result.data?.sentTo || emailOnFile);
        } catch (err) {
            console.error("Error sending portal invite:", err);
            setError(err.message || "Failed to send the portal invite.");
        } finally {
            setSending(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(magicLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-800">Client Portal Access</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    <p className="text-gray-600 text-sm">
                        <strong>{customer.name}</strong> can use a login link to see their quotes, invoices and
                        appointments without a password. Each link works for 30 days and can be used more than once,
                        so anyone who has it can open this customer's portal.
                    </p>

                    {mayInvite && (
                        <div>
                            {sentTo ? (
                                <div className="bg-green-50 text-green-800 text-sm p-3 rounded-md flex items-start gap-2">
                                    <Check size={16} className="mt-0.5" />
                                    <div>
                                        <p className="font-bold">Invite sent</p>
                                        <p>We emailed a portal login link to {sentTo}.</p>
                                    </div>
                                </div>
                            ) : emailOnFile ? (
                                <button
                                    onClick={sendInvite}
                                    disabled={sending}
                                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-md flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                                >
                                    {sending ? <Loader2 className="animate-spin" size={18} /> : <Mail size={18} />}
                                    <span className="truncate">Email invite to {emailOnFile}</span>
                                </button>
                            ) : (
                                <div className="bg-amber-50 text-amber-800 text-sm p-3 rounded-md">
                                    This customer has no email address on file. Add one to the customer record to send a portal invite.
                                </div>
                            )}
                        </div>
                    )}

                    {mayCopyLink && (
                        <div className="border-t border-gray-100 pt-5">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Or copy a link yourself</p>
                            {!magicLink ? (
                                <button
                                    onClick={generateLink}
                                    disabled={loading}
                                    className="w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold py-2.5 rounded-md flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                                >
                                    {loading ? <Loader2 className="animate-spin" size={18} /> : <Copy size={18} />}
                                    Copy link
                                </button>
                            ) : (
                                <div className="space-y-3">
                                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 flex items-center justify-between gap-2">
                                        <code className="text-xs text-gray-600 truncate flex-1">{magicLink}</code>
                                        <button
                                            onClick={copyToClipboard}
                                            className="text-blue-600 hover:text-blue-800 p-1"
                                            title="Copy to Clipboard"
                                        >
                                            {copied ? <Check size={18} /> : <Copy size={18} />}
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        Send this only to the customer. It works for 30 days.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {!mayInvite && !mayCopyLink && (
                        <div className="bg-gray-50 text-gray-600 text-sm p-3 rounded-md">
                            Your role can't share portal access. Ask an Owner, Admin or Manager.
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 text-red-600 text-sm p-3 rounded-md">
                            {error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PortalInviteModal;
